import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';

import { UsersService } from '@app/services/settings/users.service';
import { GroupsService } from '@app/services/settings/groups.service';
import { Group } from '@core/objects/group';
import { MultiSelectItem } from '@core/models/select-item.model';

import { HttpStatus } from '@core/classes/http-status.class';
import { HttpRequestStatus } from '@core/classes/http-request-status.class';
import { WysiwygData } from '@core/models/wysiwyg-data.model';

@Component({
    selector: 'settings-users-invitations-add',
    templateUrl: './invitations-add.component.html',
    styleUrls: ['./invitations-add.component.scss']
})
export class SettingsUsersInvitationsAddComponent implements OnInit, OnDestroy{
	public readonly VIEW_MODAL_NONE = 0;
	public readonly VIEW_MODAL_LOAD_GROUPS_ERROR = 1;
	public readonly VIEW_MODAL_ADD_INVITE_USER_SUCCESS = 2;
	public readonly VIEW_MODAL_ADD_INVITE_USER_ERROR = 3;

	public modalView: number= this.VIEW_MODAL_NONE;

	public form: FormGroup;
	public groups: MultiSelectItem[] = null;
	public initialGroups: Group[];
	public message: string = '';
	public selectedGroup = [];

	public modalErrorText:string = "";

	public subscriptionGetGroups: Subscription;
	public subscriptionStatusGetGroups: Subscription;
	public subscriptionStatusAddInv: Subscription;

	public buttonDisabled: boolean = false;

	public httpStatusGetGroups:HttpStatus;
	public httpStatusAddInvite:HttpRequestStatus;

	public errorAddInvite :boolean = true;
	public sent: boolean= false;

	public errorLowerCase:boolean = false;
	public placeholder: string = "Tell the recipient what they are receiving";

	@Output() cancelForm = new EventEmitter<any>();
	@Output() newInvitation = new EventEmitter<any>();

	constructor(public userSrv: UsersService, public groupsSrv: GroupsService, private formBuilder: FormBuilder){}

	ngOnInit(){
		this.form = this.formBuilder.group({
            name: ['', [Validators.required]],
            email: ['', Validators.compose([
				Validators.required,
				Validators.email
			 ])],
		});


		this.subscriptionGetGroups = this.groupsSrv.selectorGetAllGroups().subscribe((groups: Group[]) => {
			if (groups != null) { // return on fornt only active groups
				this.initialGroups = groups.filter((g: Group) => g.active);
				this.createSelectGroup();
			} else {
				this.groupsSrv.loadAllGroups();
			}
		});
		this.subscriptionStatusGetGroups = this.groupsSrv.selectorStatusGroups().subscribe((status: HttpStatus) => {
			this.httpStatusGetGroups = status;
			if(status.code == HttpStatus.ERROR)
				this.modalView = this.VIEW_MODAL_LOAD_GROUPS_ERROR;
		});

		this.subscriptionStatusAddInv = this.userSrv.selectorStatusAddInvite().subscribe((status: HttpRequestStatus) => {
			this.httpStatusAddInvite = status;
			switch (this.httpStatusAddInvite.code) {
				case HttpRequestStatus.READY: 
					// initial status
					this.cleanForm();
					break;
				case HttpRequestStatus.PENDING:
					// Loadings
					this.buttonDisabled = true;
					break;
				case HttpRequestStatus.DONE:
					// Success
					this.cleanForm();
					this.modalView = this.VIEW_MODAL_ADD_INVITE_USER_SUCCESS;
					this.buttonDisabled = false;
					this.errorAddInvite = false;
					break;
				case HttpRequestStatus.ERROR:
					// Error - type Error
					this.modalView = this.VIEW_MODAL_ADD_INVITE_USER_ERROR;
					this.errorAddInvite = true;
					this.buttonDisabled = false;
					this.controlErrors();
					break;
			}
		});
	}

	ngOnDestroy(){
		this.subscriptionGetGroups.unsubscribe();
		this.subscriptionStatusAddInv.unsubscribe();
		this.subscriptionStatusGetGroups.unsubscribe();
		this.groupsSrv.clear();
	}

	checkUpperCase(event){
		(event.target.value.toLowerCase() == event.target.value) || event.target.value === '' ? this.errorLowerCase = false : this.errorLowerCase = true;
	}

	controlErrors(){
		console.log(this.httpStatusAddInvite.message);
		switch (this.httpStatusAddInvite.message){
			case "403":
				this.modalErrorText = "You have no permissions to perform this action. Try cancelling and starting again, or contact support"
				break;
			case "409":
				this.modalErrorText = "Oops! It looks like this user is already a member or that an invitation is still pending"
				break;
			default:
				this.modalErrorText = "An unexpected error has occurred sending the invitation. Try cancelling and starting again, or contact support"
				break;
		}
	}

	/**
	 * initialize the variable MultiSelectItem array 
	 * containing the options sent to multiple selector 
	 * Need initialization to set value selected as we want, 
	 * only selected the first item
	 */
	createSelectGroup(){
		if( !this.initialGroups || this.initialGroups == null) // avoy when first clean
			return

		let newSelect = this.initialGroups.map(g => {
			return {
				title: g.name,
				value: g.id,
				selected: false,
				disabled: false
			};
		});
		// choose the first oneas default
		if(newSelect.length > 0){
			newSelect[0].selected = true;
			// need to set this selected to the current selectedGroup values to send.
			this.setGroupSelected([newSelect[0].value]);
		}
		this.groups = newSelect;

	}

	setGroupSelected(groups){
		if(groups instanceof Array){
			this.selectedGroup = groups;
		} else {
			this.selectedGroup.push(groups.value);
		}
	}

	onSubmit(){
		if(this.form.invalid)
			return;

		let data = {
			name: this.form.value.name,
			email: this.form.value.email,
			message: this.message,
			groups: this.selectedGroup,
		};
		this.sent = true;
		this.newInvitation.emit(data);
	}

	cleanForm(){
		this.createSelectGroup(); // reset the seleted items in MultiSelectItem array 
		this.selectedGroup = [];
		this.message = '';
		this.form.reset({value: ''});
	}
	// close add users screen and go back to user list
	cancel(){
		let cancel = true;
		this.errorAddInvite = false;
		this.cleanForm();
		this.cancelForm.emit(cancel);
	}

	onInviteSuccess(){
		this.userSrv.clearAddNewInvitation(); // reset the HttpRequestStatus to INITIAL in the store
		this.cancel();
	}

	// for ERROR dismiss only! 
	//  don't clean form when dismiss error modal 
	dismiss(){
		this.modalView= this.VIEW_MODAL_NONE;
		this.errorAddInvite = false;
	}

	tryAgain(){
		this.modalView= this.VIEW_MODAL_NONE;
		this.groupsSrv.loadAllGroups();
	}

	public onMessageChange(editorData: WysiwygData){
		if(editorData.text.trim() != ''){
				this.message = editorData.html;
		}
	}

}
